/**
 * AmazonProductNode - メモに Amazon 商品カードを埋め込む Tiptap カスタムノード。
 *
 * - `atom: true` の block ノードとしてスナップショットを保存する
 * - 属性はすべて data-* にシリアライズし、Tiptap JSON に平坦に格納する
 * - 表示 (NodeView) と再取得ボタンは `amazon-product-node-view.tsx` を参照
 */

import { Node, mergeAttributes } from '@tiptap/core'
import type { NodeViewProps } from '@tiptap/react'
import { ReactNodeViewRenderer } from '@tiptap/react'

import { AmazonProductNodeView } from './amazon-product-node-view'

export interface AmazonProductAttributes {
  asin: string | null
  url: string
  title: string
  image: string | null
  price: string | null
  brand: string | null
  rating: number | null
  reviewCount: number | null
  /** ISO 8601 (UTC) */
  fetchedAt: string
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    amazonProduct: {
      /** カーソル位置に Amazon 商品カードを挿入する */
      insertAmazonProduct: (attrs: AmazonProductAttributes) => ReturnType
    }
  }
}

export const AmazonProductNode = Node.create({
  name: 'amazonProduct',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      asin: {
        default: null as string | null,
        parseHTML: (el) => el.getAttribute('data-asin'),
        renderHTML: (attrs) => (attrs.asin ? { 'data-asin': String(attrs.asin) } : {}),
      },
      url: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-url') ?? '',
        renderHTML: (attrs) => ({ 'data-url': String(attrs.url ?? '') }),
      },
      title: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-title') ?? '',
        renderHTML: (attrs) => ({ 'data-title': String(attrs.title ?? '') }),
      },
      image: {
        default: null as string | null,
        parseHTML: (el) => el.getAttribute('data-image'),
        renderHTML: (attrs) => (attrs.image ? { 'data-image': String(attrs.image) } : {}),
      },
      price: {
        default: null as string | null,
        parseHTML: (el) => el.getAttribute('data-price'),
        renderHTML: (attrs) => (attrs.price ? { 'data-price': String(attrs.price) } : {}),
      },
      brand: {
        default: null as string | null,
        parseHTML: (el) => el.getAttribute('data-brand'),
        renderHTML: (attrs) => (attrs.brand ? { 'data-brand': String(attrs.brand) } : {}),
      },
      rating: {
        default: null as number | null,
        parseHTML: (el) => {
          const raw = el.getAttribute('data-rating')
          if (!raw) return null
          const n = Number(raw)
          return Number.isFinite(n) ? n : null
        },
        renderHTML: (attrs) => (attrs.rating != null ? { 'data-rating': String(attrs.rating) } : {}),
      },
      reviewCount: {
        default: null as number | null,
        parseHTML: (el) => {
          const raw = el.getAttribute('data-review-count')
          if (!raw) return null
          const n = Number(raw)
          return Number.isFinite(n) ? n : null
        },
        renderHTML: (attrs) =>
          attrs.reviewCount != null ? { 'data-review-count': String(attrs.reviewCount) } : {},
      },
      fetchedAt: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-fetched-at') ?? '',
        renderHTML: (attrs) => ({ 'data-fetched-at': String(attrs.fetchedAt ?? '') }),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'div[data-type="amazon-product"]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': 'amazon-product' }, HTMLAttributes)]
  },

  addNodeView() {
    return ReactNodeViewRenderer(AmazonProductNodeView as (props: NodeViewProps) => React.JSX.Element)
  },

  addCommands() {
    return {
      insertAmazonProduct:
        (attrs: AmazonProductAttributes) =>
        ({ chain }) =>
          chain().insertContent({ type: this.name, attrs }).run(),
    }
  },
})
